// FIG Indicators (DESIGN_SPEC §11.4.9, §10.11, §12.9). Props: FigProps { ctx, no }.
//   meta(ctx) → { id: "indicators", label: "Indicators", count: shown, empty: nothing at all }
//   Network | Host, one hairline table per kind: value (middle-truncated; paths keep their file name) · hits · seen in
//   (event kinds in words; "static" for values only the static report produced, and static strings that read like code
//   namespaces say so) · copy. Each table has "copy all" (Shift copies JSON); a %TEMP%\tmp*.tmp burst folds into one
//   expandable row; Windows background and the guest's own subnet fold into a counted footer per table ("show").
//   Figure actions: Copy N (Shift: JSON) · JSON · CSV (kind,value,hits,seen_in,static,background).
//   IndicatorsLive ({ ctx })   the cockpit's "Indicators so far": the store's preview, the same tables, nothing scored.
import { useEffect, useRef, useState } from "react";
import { Button, CopyButton, CopyText, FigHead, MT, figTitleId } from "@/components/ui";
import { copyText } from "@/lib/clipboard";
import { count } from "@/lib/format";
import type { FigMeta, FigProps, ReportCtx } from "@/lib/derive/types";
import { backgroundWords, iocDisplay, iocTail, iocValues, iocsCsv, iocsJson, iocsOf, type IocItem, type IocSet, type IocTable } from "../derive/iocs";

export function meta(ctx: ReportCtx): FigMeta {
  const s = iocsOf(ctx);
  return { id: "indicators", label: "Indicators", count: s.shown, empty: s.shown + s.background === 0 };
}

function saveText(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function CopyAll({ tables, n }: { tables: readonly IocTable[]; n: number }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const values = iocValues(tables).map((r) => r.value);
  return (
    <>
      <Button
        size="sm"
        icon="copy"
        disabled={!n}
        title="Copy every value shown, one per line (Shift: JSON)"
        onClick={async (e) => {
          const ok = await copyText(e.shiftKey ? iocsJson(tables, false) : values.join("\n"));
          setState(ok ? "copied" : "failed");
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => setState("idle"), 1200);
        }}
      >
        {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : `Copy ${count(n)}`}
      </Button>
      <span className="sr-only" role="status">
        {state === "copied" ? `${count(n)} indicators copied` : ""}
      </span>
    </>
  );
}

function Exports({ tables, n, name }: { tables: readonly IocTable[]; n: number; name: string }) {
  return (
    <>
      <CopyAll tables={tables} n={n} />
      <Button size="sm" icon="download" title="The report's shape: { iocs: { kind: [...] } }, background included" onClick={() => saveText(`${name}-iocs.json`, iocsJson(tables), "application/json")}>
        JSON
      </Button>
      <Button size="sm" icon="download" title="kind, value, hits, seen_in, static, background" onClick={() => saveText(`${name}-iocs.csv`, iocsCsv(tables), "text/csv")}>
        CSV
      </Button>
    </>
  );
}

function ValueCell({ r, child, open, onToggle }: { r: IocItem; child?: boolean; open?: boolean; onToggle?: () => void }) {
  const display = iocDisplay(r.kind, r.value);
  return (
    <td className="primary w-full max-w-0">
      <div className="flex min-w-0 items-center gap-1.5">
        {child && <span className="elbow" aria-hidden="true" />}
        {r.children && (
          <button type="button" className="inline-flex h-5 w-4 flex-none items-center justify-center text-ink-mute hover:text-ink" aria-expanded={open} aria-label={open ? "Hide the folded paths" : "Show the folded paths"} onClick={onToggle}>
            <span className={`caret ${open ? "is-open" : ""}`} aria-hidden="true" />
          </button>
        )}
        <MT value={display} tail={iocTail(r.kind, display)} className={r.children ? "flex-none" : "min-w-0"} />
        {r.foldLabel && <span className="min-w-0 truncate text-ink-faint">· {r.foldLabel}</span>}
      </div>
      {r.note && <div className="pb-1 font-sans text-[12.5px] leading-snug text-ink-mute">{r.note}</div>}
    </td>
  );
}

function IocRowView({ r, label, child, background }: { r: IocItem; label: string; child?: boolean; background?: boolean }) {
  const [open, setOpen] = useState(false);
  const copyValue = r.children ? r.children.map((c) => c.value).join("\n") : r.value;
  return (
    <>
      <tr className={background ? "[&_td]:text-ink-mute" : undefined}>
        <ValueCell r={r} child={child} open={open} onToggle={() => setOpen((o) => !o)} />
        <td className="r">{count(r.hits)}</td>
        <td className="text-ink-mute">
          {r.seenIn.join(" · ") || "—"}
          {background && r.service && <span className="text-ink-faint"> · {r.service}</span>}
        </td>
        <td className="r">
          <CopyButton value={copyValue} label={r.children ? `${r.children.length} ${label}s` : label} />
        </td>
      </tr>
      {open && r.children?.map((c) => <IocRowView key={c.value} r={c} label={label} child />)}
    </>
  );
}

function IocTableView({ t }: { t: IocTable }) {
  const [showBg, setShowBg] = useState(false);
  const shown = iocValues([t]);
  const values = shown.map((r) => r.value).join("\n");
  return (
    <table className="t t--sm">
      <thead>
        {shown.length > 0 ? (
          <tr>
            <th>
              {t.label} <span className="tracking-[.04em]">{count(shown.length)}</span>
            </th>
            <th className="r">hits</th>
            <th>seen in</th>
            <th className="r">
              <CopyText value={values} altValue={iocsJson([t], false)} label={`${t.label} values`} />
            </th>
          </tr>
        ) : (
          <tr>
            <th colSpan={4}>{t.label} · background only</th>
          </tr>
        )}
      </thead>
      <tbody>
        {t.rows.map((r) => (
          <IocRowView key={r.value} r={r} label={t.label} />
        ))}
        {t.background.length > 0 && (
          <tr>
            <td colSpan={4} className="whitespace-normal py-1.5">
              <span className="font-medium tracking-[.14em] text-ink-faint uppercase">Background · {count(t.background.length)}</span>
              <span className="text-ink-mute"> · {backgroundWords(t.background)} · </span>
              <button type="button" className="lnk" aria-expanded={showBg} onClick={() => setShowBg((s) => !s)}>
                {showBg ? "hide" : "show"}
              </button>
            </td>
          </tr>
        )}
        {showBg && t.background.map((r) => <IocRowView key={`bg:${r.value}`} r={r} label={t.label} background />)}
      </tbody>
    </table>
  );
}

function Side({ title, tables, live }: { title: string; tables: readonly IocTable[]; live?: boolean }) {
  const shown = iocValues(tables).length;
  const bg = tables.reduce((s, t) => s + t.background.length, 0);
  const stat = iocValues(tables).filter((r) => r.static).length;
  return (
    <div className="min-w-0">
      <p className="subhead">
        {title}
        <span className="ct">
          {count(shown - stat)} {live ? "so far" : "observed"}
          {stat > 0 && ` · ${count(stat)} static`}
          {bg > 0 && ` · ${count(bg)} background`}
        </span>
      </p>
      {tables.length ? (
        <div className="grid gap-5">
          {tables.map((t) => (
            <div key={t.kind} className="tscroll relative">
              <IocTableView t={t} />
            </div>
          ))}
        </div>
      ) : (
        <p className="quiet whitespace-normal">None{live ? " yet" : ""}.</p>
      )}
    </div>
  );
}

function Body({ set, live }: { set: IocSet; live?: boolean }) {
  return (
    <div className="grid grid-cols-2 max-[900px]:grid-cols-1">
      <div className="min-w-0 pr-cell max-[900px]:pr-0">
        <Side title="Network" tables={set.network} live={live} />
      </div>
      <div className="min-w-0 border-l border-rule-cell pl-cell max-[900px]:mt-8 max-[900px]:border-l-0 max-[900px]:pl-0">
        <Side title="Host" tables={set.host} live={live} />
      </div>
    </div>
  );
}

export default function IndicatorsFig({ ctx, no }: FigProps) {
  const live = ctx.mode === "live";
  const set = iocsOf(ctx);
  const tables = [...set.network, ...set.host];
  return (
    <section className="cell fig" aria-labelledby={figTitleId(no)}>
      <FigHead
        no={no}
        label="Indicators"
        count={`${count(set.shown)} shown · ${count(set.background)} background`}
        actions={<Exports tables={tables} n={set.shown} name={ctx.job.id} />}
      />
      <div className="fig-body">
        <Body set={set} live={live} />
      </div>
    </section>
  );
}

/** The cockpit's "Indicators so far": the store's preview grouped like the report, background kept apart. */
export function IndicatorsLive({ ctx }: { ctx: ReportCtx }) {
  const set = iocsOf(ctx);
  const tables = [...set.network, ...set.host];
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2.5">
        <p className="meta">
          {count(set.shown)} so far · {count(set.background)} background
        </p>
        <div className="flex flex-wrap gap-2">
          <Exports tables={tables} n={set.shown} name={ctx.job.id} />
        </div>
      </div>
      <div className="mt-5">
        <Body set={set} live />
      </div>
    </div>
  );
}
